/** @file Structural shape of a clip document (design 4.4, 17); frame rules are checked by validateClip. */
import { checkArray } from '@zilo/pose3d-format/validate/fields/checkArray';
import { checkBoolean } from '@zilo/pose3d-format/validate/fields/checkBoolean';
import { checkEnum } from '@zilo/pose3d-format/validate/fields/checkEnum';
import { checkId } from '@zilo/pose3d-format/validate/fields/checkId';
import { checkNumber } from '@zilo/pose3d-format/validate/fields/checkNumber';
import { checkRecord } from '@zilo/pose3d-format/validate/fields/checkRecord';
import { checkShape } from '@zilo/pose3d-format/validate/fields/checkShape';
import { checkString } from '@zilo/pose3d-format/validate/fields/checkString';
import { checkTuple } from '@zilo/pose3d-format/validate/fields/checkTuple';
import type { FieldCheck, Shape } from '@zilo/pose3d-format/validate/fields/fieldCheck';
import { NAME_PATTERN } from '@zilo/pose3d-format/validate/fields/namePattern';

const name = checkString(NAME_PATTERN);
const frame = checkNumber({ int: true, min: 0 });
const ease = checkEnum(['linear', 'easeIn', 'easeOut', 'easeInOut', 'step']);
const hold = checkNumber({ int: true, min: 1 });
const flexParent: FieldCheck = (value, path) => { if (value !== null) name(value, path); };
const steps = checkNumber({ int: true, min: 1, max: 360 });
/** 1..360 steps per turn, or `free` (design 17.3, R1). */
const facingSteps: FieldCheck = (value, path) => { if (value !== 'free') steps(value, path); };

const NODE_KEY = checkShape({
  required: {},
  optional: {
    rot: checkTuple(3), pos: checkTuple(3), scale: checkNumber({ above: 0 }), parent: flexParent,
    rotToggle: checkBoolean, hidden: checkBoolean, ease, hold,
  },
});

const EVENT = checkShape({
  required: { type: checkEnum(['step', 'hook1', 'hook2', 'hook3', 'hit', 'sound', 'custom']) },
  optional: { node: name, mode: checkEnum(['fixed', 'follow', 'stop']), pos: checkTuple(3), payload: () => undefined },
});

const KEY = checkShape({
  required: { frame, nodes: checkRecord(NODE_KEY) },
  optional: { ease, hold, events: checkArray(EVENT) },
});

/** Shape of `Clip`. */
export const CLIP_SHAPE: Shape = {
  required: {
    format: checkEnum(['pose3d/clip@1']), id: checkId('clp'), name: checkString(), fps: checkNumber({ above: 0 }),
    frames: checkNumber({ int: true, min: 1 }), loopStart: frame, start: frame, loop: checkBoolean, mirrorHalf: checkBoolean,
    category: checkString(), keys: checkArray(KEY),
  },
  optional: {
    facingSteps,
    tracks: checkRecord(checkShape({ required: {}, optional: { frameKey: checkArray(frame), hold, visible: checkBoolean } })),
  },
};
